<script setup lang="ts">
import { computed } from 'vue';
import { Dialog } from 'quasar';
import { ITicketTransactionContext } from '@/components/TicketsAccountingPeriod/ticketsTypes.ts';
import { TransactionTypeEnum } from '@/components/TableTransactions/transactionsTypes.ts';
import {
  formatTransactionAmount,
  getTransactionTypeClass,
  formaterCounterparty,
} from '@/helpers/transactionUtils.ts';
import { useRouter } from 'vue-router';

const props = withDefaults(defineProps<{
  transactionContext?: ITicketTransactionContext | null;
  companyId?: string;
  folderId?: string;
  periodFolderId?: string;
  isLoading?: boolean;
  canUnlink?: boolean;
  hideNavigation?: boolean;
}>(), {
  companyId: '',
  folderId: '',
  periodFolderId: '',
  transactionContext: null,
  isLoading: false,
  canUnlink: false,
  hideNavigation: false,
});

const emit = defineEmits<{(e: 'unlinkTransaction', transactionId: string): void}>();

const router = useRouter();

const isNavigationVisible = computed(() => {
  return !props.hideNavigation && !!(
    props.companyId &&
    props.folderId &&
    props.periodFolderId &&
    props.transactionContext
  );
});

const goToTransactions = () => {
  if (!props.companyId || !props.periodFolderId || !props.folderId) return;
  router.push({
    name: 'AccountingPeriodTransactionsPage',
    params: {
      company_id: props.companyId,
      folder_id: props.folderId,
      period_folder_id: props.periodFolderId,
    },
  });
};

const handleUnlinkTransaction = () => {
  const transactionId = props.transactionContext?.transactionId;
  if (!transactionId) return;

  Dialog.create({
    title: 'Confirm Unlink',
    message: 'Are you sure you want to unlink this transaction from the question?',
    persistent: true,
    ok: {
      label: 'Unlink',
      color: 'negative',
      flat: true,
      noCaps: true,
    },
    cancel: {
      label: 'Cancel',
      color: 'primary',
      flat: true,
      noCaps: true,
    },
  }).onOk(() => {
    emit('unlinkTransaction', transactionId);
  });
};

const formatDate = (date: string | null | undefined) => {
  if (!date) return '';
  return new Date(date).toLocaleDateString();
};
</script>

<template>
  <div
    v-if="props.transactionContext"
    class="q-mb-md"
  >
    <div class="text-body1 q-mb-sm text-grey-9 text-weight-medium flex items-center justify-between">
      <div class="flex items-center">
        <q-icon
          name="swap_horiz"
          size="xs"
          class="q-mr-xs"
        />
        Linked Transaction
      </div>
      <div class="row items-center no-wrap q-gutter-x-sm">
        <q-btn
          v-if="isNavigationVisible"
          flat
          round
          color="dark"
          icon="open_in_new"
          size="sm"
          @click="goToTransactions"
        >
          <q-tooltip class="text-body2">
            Go to transactions
          </q-tooltip>
        </q-btn>
        <q-btn
          v-if="props.canUnlink"
          flat
          round
          color="dark"
          icon="link_off"
          size="sm"
          :loading="props.isLoading"
          :disable="props.isLoading"
          @click.stop="handleUnlinkTransaction"
        >
          <q-tooltip class="text-body2">
            Unlink transaction
          </q-tooltip>
        </q-btn>
      </div>
    </div>
    <div class="rounded-borders bg-white q-pa-md bordered">
      <div class="row q-col-gutter-md">
        <div class="col-12 col-sm-6">
          <div class="text-caption text-grey-7">
            Date
          </div>
          <div class="text-body2">
            {{ formatDate(props.transactionContext.transactionDate) }}
          </div>
        </div>
        <div class="col-12 col-sm-6 text-right">
          <div class="text-caption text-grey-7">
            Amount
          </div>
          <div
            class="text-body2 text-weight-bold"
            :class="getTransactionTypeClass(props.transactionContext.transactionType as TransactionTypeEnum)"
          >
            {{ formatTransactionAmount(props.transactionContext.amount, props.transactionContext.currency, props.transactionContext.transactionType as TransactionTypeEnum) }}
          </div>
        </div>

        <div
          v-if="formaterCounterparty(props.transactionContext.counterpartyName, props.transactionContext.counterpartyId, props.transactionContext.counterparty)"
          class="col-12"
        >
          <div class="text-caption text-grey-7">
            Counterparty
          </div>
          <div class="text-body2">
            {{ formaterCounterparty(props.transactionContext.counterpartyName, props.transactionContext.counterpartyId, props.transactionContext.counterparty) }}
            <span
              v-if="props.transactionContext.counterpartyIban"
              class="text-grey-6"
            > ({{ props.transactionContext.counterpartyIban }})</span>
          </div>
        </div>

        <div
          v-if="props.transactionContext.account"
          class="col-12"
        >
          <div class="text-caption text-grey-7">
            Bank Account
          </div>
          <div class="text-body2">
            {{ props.transactionContext.account.accountName }}
            <span
              v-if="props.transactionContext.account.iban"
              class="text-grey-6"
            > ({{ props.transactionContext.account.iban }})</span>
          </div>
        </div>
        <div
          v-if="props.transactionContext.note"
          class="col-12"
        >
          <div class="text-caption text-grey-7">
            Description
          </div>
          <div class="text-body2">
            {{ props.transactionContext.note }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.bordered {
  border: 1px solid #e0e0e0;
}
</style>
